import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Multi-select over the cards shown (design: checkboxes, drag-box, Select all;
 * decision 20: Shift-click ranges, Cmd/Ctrl-click toggles, Cmd-A, Esc clears).
 * `keys` are the shown cards in order; selection never reaches past them.
 */
export function useSelection<K extends string | number>(keys: K[], enabled: boolean) {
  const [picked, setPicked] = useState<K[]>([])
  const anchor = useRef<K | null>(null)

  const toggle = useCallback((k: K) => {
    anchor.current = k
    setPicked((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]))
  }, [])

  const range = useCallback(
    (k: K) => {
      const from = anchor.current === null ? -1 : keys.indexOf(anchor.current)
      const to = keys.indexOf(k)
      if (from < 0 || to < 0) return toggle(k)
      const span = keys.slice(Math.min(from, to), Math.max(from, to) + 1)
      setPicked((p) => [...p, ...span.filter((x) => !p.includes(x))])
    },
    [keys, toggle],
  )

  const selectAll = useCallback(() => setPicked([...keys]), [keys])
  const clear = useCallback(() => {
    anchor.current = null
    setPicked([])
  }, [])
  const replace = useCallback((next: K[]) => setPicked(next), [])

  /** A card click: Shift ranges, Cmd/Ctrl toggles; otherwise false (the caller opens it). */
  const clickWithKeys = useCallback(
    (k: K, e: { shiftKey: boolean; metaKey: boolean; ctrlKey: boolean }) => {
      if (!enabled) return false
      if (e.shiftKey) return range(k), true
      if (e.metaKey || e.ctrlKey) return toggle(k), true
      return false
    },
    [enabled, range, toggle],
  )

  useEffect(() => {
    if (!enabled) return
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      if (target?.closest('input, textarea, [contenteditable="true"]')) return
      if (document.querySelector('[role="dialog"], [role="menu"]')) return
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'a') {
        e.preventDefault()
        selectAll()
      } else if (e.key === 'Escape') clear()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [enabled, selectAll, clear])

  // Cards that left the screen (filter change, hidden, removed) drop out.
  useEffect(() => {
    setPicked((p) => {
      const kept = p.filter((k) => keys.includes(k))
      return kept.length === p.length ? p : kept
    })
  }, [keys])

  return { picked, toggle, range, selectAll, clear, replace, clickWithKeys }
}
